import React from 'react';
import { Shield, Lock, Globe, FileText, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white pt-32 pb-24 px-4 font-sans text-slate-900">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="border-b border-slate-200 pb-8 mb-10">
            <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-blue-600 rounded-lg text-white">
                    <Shield className="h-6 w-6" />
                </div>
                <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">Privacy Policy</h1>
            </div>
            <p className="text-xl text-slate-500">
                Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
        </div>
        
        {/* Content */}
        <div className="prose prose-slate prose-lg max-w-none text-slate-600 leading-relaxed">
          
          {/* 1. Introduction */}
          <section className="mb-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 mb-4">
               1. Introduction
            </h2>
            <p className="mb-4">
              1.1 This Privacy Policy (hereinafter: "Policy") is used by <strong>ThreeDotsOnline LTD</strong>, trading under the name Searchy.io, a company with limited liability under the laws of Israel, with its registered office and primary place of business at Helkiki Haaor 16 Beer Sheva, Israel, and registered in the register of the Israel Chamber of Commerce (hereinafter: "Searchy.io" or "we"). Searchy.io can be reached via our Contact Form.            </p>
            <p className="mb-4">
              1.2 Searchy.io is committed to protecting your privacy. This Policy describes what we do with your data and applies to all information that identifies you or may be used to identify you with ("Personal data") by Searchy.io, such as but not limited to processing via the Searchy.io websites (hereinafter: "Websites"). The Websites are intended for general audiences over the age of 16 years old. We do not knowingly collect information from children under the age of 16 years old.
            </p>
            <p className="mb-4">
              1.3 If you are not at least 16 years old (or 13 years old in the United States), please do not use the Websites. Our Services are not intended for use by children under these ages. If we learn we have collected or received personal information from a child under the mentioned age without verification of parental consent, we will delete that information. If you believe we might have any information from or about a child under the age, please contact us.
            </p>
            <p className="mb-4">
              1.4 This Privacy Policy is provided in English. Translations to certain other languages may be available and can be requested by contacting us.
            </p>
          </section>

          {/* 2. Processing */}
          <section className="mb-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 mb-4">
               2. Processing of Personal Data
            </h2>
            <p className="mb-4">
              2.1 Searchy.io is a data controller under the General Data Protection Regulation (GDPR). This means that with regard to any Personal data referred to in this Policy, Searchy.io determines the purpose for which we process this data and we determine the means necessary for the processing of this data. As a data controller, we process the data set out in this section. Searchy.io does not engage in automated decision-making.
            </p>

            <h3 className="text-lg font-bold text-slate-800 mt-6 mb-3">Data collected directly from you</h3>
            <p className="mb-4">
              2.2 When you access our Websites, we may (automatically) collect the following information about you and/or your device:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1 bg-slate-50 p-6 rounded-2xl border border-slate-100">
              <li>Your anonymous IP address;</li>
              <li>Your (mobile) device identifier;</li>
              <li>Information about your settings, such as language settings, browser information (user agent) and/or operating system;</li>
              <li>Query-string;</li>
              <li>Your Website preferences;</li>
              <li>Your location and time zone.</li>
            </ul>

            <h3 className="text-lg font-bold text-slate-800 mt-6 mb-3">Data you may provide to us</h3>
            <p className="mb-4">
              2.3 Access to the Websites, their content and any web search results generated by using the Websites in general do not require you to submit Personal data to us other than the information listed above. Under certain circumstances, you may wish to provide us with more information about yourself, for example in relation to any enquiry you may want to make with us via our Contact Form.
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                <li>Your name;</li>
                <li>Your company name;</li>
                <li>Your email address;</li>
                <li>Any other Personal data you provide us with.</li>
            </ul>
            
            <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 text-sm">
                <p className="font-bold text-blue-900 mb-2">Note regarding Google reCAPTCHA:</p>
                <p className="mb-2">
                    For our website www.searchy.io we use Google reCAPTCHA before you can fill in our Contact Form. It is a system to find out if the website visitor is a human and not a bot. You acknowledge and understand that the reCAPTCHA API collects information including IP address, mouse movements, and browser addons.
                </p>
                <p>
                    This data is sent to Google for analysis. The information collected will be used for improving reCAPTCHA and for general security purposes. It will not be used for personalised advertising by Google.
                </p>
            </div>
          </section>

          {/* 3. Purposes */}
          <section className="mb-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 mb-4">
               3. Purposes of the Processing
            </h2>
            <p className="mb-4">
              3.1 Primary purposes for which we process your Personal data are to offer you the best services possible, to optimize our Websites and services and to ensure continuity thereof. More specifically:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>To provide our services:</strong> Data is used to properly perform our services. User behavior is never tracked to match organic results or ads to the visitor's profile. Organic results and ads are only shown based on the visitor's search query.</li>
                <li><strong>To improve our services:</strong> We are constantly working on improving your user experience and adding new functionality.</li>
                <li><strong>To secure our services:</strong> To keep out hackers, automated bots, or other threats.</li>
                <li><strong>To communicate with you:</strong> When you contact us, we process your contact details to adequately revert to you.</li>
            </ul>
          </section>

          {/* 4. Rights (Fixed Numbering) */}
          <section className="mb-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 mb-4">
               4. Your Rights
            </h2>
            <p className="mb-4">4.1 You are provided with the following rights:</p>
            <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 p-4 rounded-xl">
                    <strong className="block text-slate-900 mb-1">Objection</strong>
                    <span className="text-sm">You have the right to object to the processing of your Personal data.</span>
                </div>
                <div className="bg-white border border-slate-200 p-4 rounded-xl">
                    <strong className="block text-slate-900 mb-1">Access</strong>
                    <span className="text-sm">You have the right to request confirmation and information on the purposes for which Personal data is processed.</span>
                </div>
                <div className="bg-white border border-slate-200 p-4 rounded-xl">
                    <strong className="block text-slate-900 mb-1">Rectification & Erasure</strong>
                    <span className="text-sm">You have the right to rectification or deletion of your Personal data that is incomplete or inaccurate.</span>
                </div>
                <div className="bg-white border border-slate-200 p-4 rounded-xl">
                    <strong className="block text-slate-900 mb-1">Data Portability</strong>
                    <span className="text-sm">You have the right to receive your Personal data in a structured, commonly used format.</span>
                </div>
            </div>
          </section>

          {/* 5. Contact */}
          <section className="mt-12 pt-8 border-t border-slate-200">
             <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900 mb-4">
               5. Contact Us
            </h2>
             <p className="mb-4">If you have any questions about this Privacy Policy, please contact us at:</p>
             <a href="mailto:privacy@searchy.io" className="flex items-center gap-2 text-blue-600 font-bold hover:text-blue-800 transition-colors">
                <Mail className="h-5 w-5" /> privacy@searchy.io
             </a>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;